<template>
  <v-container style="max-width: 960px">
    <v-row>
      <v-col cols="12">
        
        <div class="d-md-none mb-6">
          <h1 class="text-h5 mb-3">{{ $t('shop.userDetails.title') }}</h1>
          <v-tabs v-model="activeTab" show-arrows>
            <v-tab
              v-for="section in sections"
              :key="section.id"
              @click="goTo(section.id)"
            >
              {{ $t(section.label) }}
            </v-tab>
          </v-tabs>
        </div>

        <h1 class="text-h5 mb-4 d-none d-md-block">{{ $t('shop.userDetails.title') }}</h1>

        <!-- Profile header -->
        <v-card outlined class="mb-8">
          <v-card-text class="d-flex align-center">
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-avatar
                  size="72"
                  class="mr-5 flex-shrink-0 avatar-upload"
                  role="button"
                  tabindex="0"
                  :aria-label="$t('shop.userDetails.tooltip.changeProfileImage')"
                  v-bind="attrs"
                  v-on="on"
                  @click="$refs.profileImgInput.$refs.input.click()"
                  @keydown.enter.prevent="$refs.profileImgInput.$refs.input.click()"
                >
                  <v-img
                    :key="avatarKey"
                    :src="imageSource(oneUser.image, 250, 250)"
                    :alt="$t('shop.userDetails.alt.profileImage')"
                  >
                    <template v-slot:placeholder>
                      <v-row class="fill-height ma-0" align="center" justify="center">
                        <v-icon size="40" color="grey lighten-1">mdi-account-circle</v-icon>
                      </v-row>
                    </template>
                  </v-img>
                  <div class="avatar-upload__overlay">
                    <v-icon size="28">mdi-camera-plus</v-icon>
                  </div>
                </v-avatar>
              </template>
              <span>{{ $t('shop.userDetails.tooltip.changeProfileImage') }}</span>
            </v-tooltip>

            <div class="min-width-0">
              <div class="text-h5">{{ oneUser.name }}</div>
              <div class="text-body-2 text--secondary">{{ get('auth_user').email }}</div>
              <v-chip small outlined color="primary darken-3" class="mt-2">
                {{ currentUser(['ROLE_END_USER']) ? $t('shop.userDetails.role.user') : $t('shop.userDetails.role.admin') }}
              </v-chip>
            </div>
          </v-card-text>

          <v-file-input
            ref="profileImgInput"
            class="d-none"
            prepend-icon=""
            hide-input
            accept="image/*"
            v-model="profileImg"
            @change="profilBildUpload()"
          />
        </v-card>

        <!-- 1. Personal data -->
        <section id="profil" class="mb-8">
          <div class="text-overline text--secondary">
            {{ $t('shop.userDetails.section.personalData') }}
          </div>
          <v-card outlined>
            <!-- Name -->
            <v-list-item class="px-6">
              <div class="d-flex align-center justify-space-between flex-grow-1">
                <span class="text--secondary">{{ $t('shop.userDetails.label.fullName') }}</span>
                <div class="d-flex align-center">
                  <span class="mr-4">{{ oneUser.name }}</span>
                  <v-btn
                    text
                    small
                    color="primary darken-3"
                    :aria-label="$t('shop.userDetails.dialog.nameTitle')"
                    @click="openEdit('name')"
                  >
                    {{ $t('shop.userDetails.button.change') }}
                  </v-btn>
                </div>
              </div>
            </v-list-item>
            <v-expand-transition>
              <div v-if="editRow === 'name'" class="px-6 pb-4">
                <v-form
                  ref="nameForm"
                  v-model="valid.name"
                  @submit.prevent="updateName"
                >
                  <v-text-field
                    v-model="tempValues.name"
                    :label="$t('shop.userDetails.label.fullName')"
                    outlined
                    dense
                    :rules="userNameRules"
                    autofocus
                  />
                  <v-btn
                    color="primary"
                    class="mr-2"
                    :disabled="!valid.name"
                    @click="updateName"
                  >
                    {{ $t('shop.userDetails.button.save') }}
                  </v-btn>
                  <v-btn text @click="closeEdit">
                    {{ $t('shop.userDetails.button.cancel') }}
                  </v-btn>
                </v-form>
              </div>
            </v-expand-transition>

            <v-divider />

            <!-- Email -->
            <v-list-item class="px-6">
              <div class="d-flex align-center justify-space-between flex-grow-1">
                <span class="text--secondary">{{ $t('shop.userDetails.label.emailAddress') }}</span>
                <div class="d-flex align-center">
                  <span class="mr-4">{{ get('auth_user').email }}</span>
                  <v-btn
                    text
                    small
                    color="primary darken-3"
                    :aria-label="$t('shop.userDetails.dialog.emailTitle')"
                    @click="openEdit('email')"
                  >
                    {{ $t('shop.userDetails.button.change') }}
                  </v-btn>
                </div>
              </div>
            </v-list-item>
            <v-expand-transition>
              <div v-if="editRow === 'email'" class="px-6 pb-4">
                <v-form
                  ref="emailForm"
                  v-model="valid.email"
                  @submit.prevent="updateEmail"
                >
                  <v-text-field
                    v-model="tempValues.email"
                    :label="$t('shop.userDetails.label.emailAddress')"
                    outlined
                    dense
                    :rules="eMailRules"
                    autofocus
                  />
                  <v-btn
                    color="primary"
                    class="mr-2"
                    :disabled="!valid.email"
                    @click="updateEmail"
                  >
                    {{ $t('shop.userDetails.button.save') }}
                  </v-btn>
                  <v-btn text @click="closeEdit">
                    {{ $t('shop.userDetails.button.cancel') }}
                  </v-btn>
                </v-form>
              </div>
            </v-expand-transition>

            <v-divider />

            <!-- Password -->
            <v-list-item class="px-6">
              <div class="d-flex align-center justify-space-between flex-grow-1">
                <span class="text--secondary">{{ $t('shop.userDetails.label.password') }}</span>
                <div class="d-flex align-center">
                  <span class="mr-4">{{ $t('shop.userDetails.passwordMask') }}</span>
                  <v-btn
                    text
                    small
                    color="primary darken-3"
                    :aria-label="$t('shop.userDetails.dialog.passwordTitle')"
                    @click="openEdit('pw')"
                  >
                    {{ $t('shop.userDetails.button.change') }}
                  </v-btn>
                </div>
              </div>
            </v-list-item>
            <v-expand-transition>
              <div v-if="editRow === 'pw'" class="px-6 pb-4">
                <v-form
                  ref="passwordForm"
                  v-model="valid.password"
                  @submit.prevent="updatePassword"
                >
                  <v-text-field
                    v-model="password"
                    :label="$t('shop.userDetails.label.newPassword')"
                    outlined
                    dense
                    :type="showPwIcon ? 'text' : 'password'"
                    :append-icon="showPwIcon ? 'mdi-eye' : 'mdi-eye-off'"
                    :rules="passwordRules"
                    autofocus
                    @click:append="showPwIcon = !showPwIcon"
                  />
                  <p class="text-caption text--secondary mt-1">
                    {{ $t('shop.userDetails.label.passwordHint') }}
                  </p>
                  <v-btn
                    color="primary"
                    class="mr-2"
                    :disabled="!valid.password"
                    @click="updatePassword"
                  >
                    {{ $t('shop.userDetails.button.save') }}
                  </v-btn>
                  <v-btn text @click="closeEdit">
                    {{ $t('shop.userDetails.button.cancel') }}
                  </v-btn>
                </v-form>
              </div>
            </v-expand-transition>

            <v-divider />

            <!-- Birthday -->
            <v-list-item class="px-6">
              <div class="d-flex align-center justify-space-between flex-grow-1">
                <span class="text--secondary">{{ $t('shop.userDetails.label.birthday') }}</span>
                <div class="d-flex align-center">
                  <span class="mr-4">{{ formDate(oneUser.birthday) }}</span>
                  <v-btn
                    text
                    small
                    color="primary darken-3"
                    :aria-label="$t('shop.userDetails.dialog.birthdayTitle')"
                    @click="openEdit('dob')"
                  >
                    {{ $t('shop.userDetails.button.change') }}
                  </v-btn>
                </div>
              </div>
            </v-list-item>
            <v-expand-transition>
              <div v-if="editRow === 'dob'" class="px-6 pb-4">
                <v-form
                  ref="birthdayForm"
                  v-model="valid.birthdate"
                  @submit.prevent="submitBirthday"
                >
                  <v-menu
                    ref="birthdateMenu"
                    v-model="birthdateMenu"
                    :close-on-content-click="false"
                    transition="scale-transition"
                    offset-y
                    min-width="auto"
                  >
                    <template v-slot:activator="{ on, attrs }">
                      <v-text-field
                        :value="dateInput(birthday)"
                        :label="$t('shop.userDetails.label.birthday')"
                        prepend-icon="mdi-calendar"
                        readonly
                        outlined
                        dense
                        v-bind="attrs"
                        v-on="on"
                        :rules="birthdayRules"
                      />
                    </template>
                    <v-date-picker
                      v-model="birthday"
                      :active-picker.sync="activePicker"
                      min="1910-01-01"
                      :max="new Date().toISOString().substr(0, 10)"
                      locale="de"
                      @change="birthdateMenu = false"
                    />
                  </v-menu>
                  <v-btn
                    color="primary"
                    class="mr-2"
                    :disabled="!valid.birthdate"
                    @click="submitBirthday"
                  >
                    {{ $t('shop.userDetails.button.save') }}
                  </v-btn>
                  <v-btn text @click="closeEdit">
                    {{ $t('shop.userDetails.button.cancel') }}
                  </v-btn>
                </v-form>
              </div>
            </v-expand-transition>

            <!-- Address data: only a tenant has it -->
            <template v-if="currentUser(['ROLE_TENANT'])">
              <template v-for="field in addressFields">
                <v-divider :key="'divider-' + field.key" />
                <v-list-item :key="field.key" class="px-6">
                  <div class="d-flex align-center justify-space-between flex-grow-1">
                    <span class="text--secondary">{{ $t(field.label) }}</span>
                    <div class="d-flex align-center">
                      <span class="mr-4">{{ get('webshop_user')[field.key] }}</span>
                      <v-btn
                        text
                        small
                        color="primary darken-3"
                        :aria-label="$t(field.title)"
                        @click="openEdit(field.key)"
                      >
                        {{ $t('shop.userDetails.button.change') }}
                      </v-btn>
                    </div>
                  </div>
                </v-list-item>
                <v-expand-transition :key="'edit-' + field.key">
                  <div v-if="editRow === field.key" class="px-6 pb-4">
                    <v-form v-model="valid[field.key]" @submit.prevent="saveWebshopField(field.key)">
                      <v-text-field
                        v-model="form[field.key]"
                        :label="$t(field.input)"
                        outlined
                        dense
                        :rules="field.rules || []"
                        autofocus
                      />
                      <v-btn
                        color="primary"
                        class="mr-2"
                        :disabled="!valid[field.key]"
                        @click="saveWebshopField(field.key)"
                      >
                        {{ $t('shop.userDetails.button.save') }}
                      </v-btn>
                      <v-btn text @click="closeEdit">
                        {{ $t('shop.userDetails.button.cancel') }}
                      </v-btn>
                    </v-form>
                  </div>
                </v-expand-transition>
              </template>
            </template>

            <v-divider />

            <!-- Active since -->
            <v-list-item class="px-6">
              <div class="d-flex align-center justify-space-between flex-grow-1">
                <span class="text--secondary">{{ $t('shop.userDetails.label.activeSince') }}</span>
                <span>{{ formDate(get('user').created) }}</span>
              </div>
            </v-list-item>
          </v-card>
        </section>

        <!-- 2. Subscription and payments -->
        <section id="abo" class="mb-8">
          <div class="text-overline text--secondary">
            {{ $t('shop.userDetails.section.subscription') }}
          </div>
          <v-card outlined>
            <p v-if="!subscriptionRows.length" class="pa-6 mb-0 text-body-2">
              {{ $t('shop.userDetails.subscription.empty') }}
            </p>

            <template v-for="(sub, index) in subscriptionRows">
              <v-divider v-if="index > 0" :key="'divider-' + sub.id" />

              <!-- Plan block -->
              <div
                :key="'plan-' + sub.id"
                class="d-flex align-center justify-space-between flex-wrap pa-6"
              >
                <div class="d-flex align-center">
                  <span class="text-h6">
                    {{ sub.plan ? sub.plan.name : $t('shop.userDetails.value.unknown') }}
                  </span>
                  <v-chip
                    small
                    class="ml-3"
                    :color="stateColor(sub.status)"
                    text-color="white"
                  >
                    {{ stateLabel(sub.status) }}
                  </v-chip>
                </div>
                <div>
                  <span class="text-h6">{{ planAmount(sub) }}</span>
                  <span class="text-caption ml-1">
                    {{ sub.plan && sub.plan.period ? sub.plan.period : $t('shop.userDetails.label.perMonth') }}
                  </span>
                </div>
              </div>

              <v-divider :key="'divider-plan-' + sub.id" />

              <!-- Access until -->
              <v-list-item :key="'access-' + sub.id" class="px-6">
                <div class="d-flex align-center justify-space-between flex-grow-1">
                  <span class="text--secondary">{{ $t('shop.userDetails.label.accessUntil') }}</span>
                  <span>
                    {{ formDate(sub.period_end) }}
                    <span
                      v-if="remainingDays(sub.period_end) !== null"
                      class="text--secondary ml-2"
                    >
                      {{ $t('shop.userDetails.value.remainingDays', { days: remainingDays(sub.period_end) }) }}
                    </span>
                  </span>
                </div>
              </v-list-item>

              <v-divider
                v-if="sub.status !== 'cancelled' && sub.status !== 'expired'"
                :key="'divider-access-' + sub.id"
              />

              <!-- Next billing -->
              <v-list-item
                v-if="sub.status !== 'cancelled' && sub.status !== 'expired'"
                :key="'billing-' + sub.id"
                class="px-6"
              >
                <div class="d-flex align-center justify-space-between flex-grow-1">
                  <span class="text--secondary">{{ $t('shop.userDetails.label.nextBilling') }}</span>
                  <span>{{ formDate(sub.period_end) }}</span>
                </div>
              </v-list-item>

              <v-divider :key="'divider-billing-' + sub.id" />

              <!-- Payment method -->
              <v-list-item :key="'payment-' + sub.id" class="px-6">
                <div class="d-flex align-center justify-space-between flex-grow-1">
                  <span class="text--secondary">{{ $t('shop.userDetails.label.paymentMethod') }}</span>
                  <span>{{ paymentMethod(sub) }}</span>
                </div>
              </v-list-item>

              <v-divider :key="'divider-payment-' + sub.id" />

              <!-- Cancelled at -->
              <v-list-item :key="'cancelled-' + sub.id" class="px-6">
                <div class="d-flex align-center justify-space-between flex-grow-1">
                  <span class="text--secondary">{{ $t('shop.userDetails.label.cancelledAt') }}</span>
                  <span>{{ formDate(sub.cancelled_at) }}</span>
                </div>
              </v-list-item>

              <!-- Cancel booked -->
              <template v-if="sub.cancel_dispatch_at">
                <v-divider :key="'divider-booked-' + sub.id" />
                <v-list-item :key="'booked-' + sub.id" class="px-6">
                  <div class="d-flex align-center justify-space-between flex-grow-1">
                    <span class="text--secondary">{{ $t('shop.userDetails.label.cancelBooked') }}</span>
                    <span>{{ formDate(sub.cancel_dispatch_at) }}</span>
                  </div>
                </v-list-item>
              </template>

              <v-card-actions
                v-if="canCancel(sub) || sub.cancel_dispatch_at || canReactivate(sub)"
                :key="'actions-' + sub.id"
                class="px-6 pb-4"
              >
                <v-btn
                  v-if="canCancel(sub)"
                  outlined
                  color="error darken-2"
                  @click="askCancel(sub.id)"
                >
                  {{ $t('shop.userDetails.button.cancelSubscription') }}
                </v-btn>
                <v-btn
                  v-if="sub.cancel_dispatch_at"
                  text
                  color="primary darken-3"
                  @click="withdrawCancel(sub.id)"
                >
                  {{ $t('shop.userDetails.button.withdrawCancel') }}
                </v-btn>
                <v-btn
                  v-if="canReactivate(sub)"
                  outlined
                  color="primary darken-3"
                  @click="reactivateSubscription(sub.id)"
                >
                  {{ $t('shop.userDetails.button.reactivateSubscription') }}
                </v-btn>
              </v-card-actions>
            </template>
          </v-card>
        </section>

        <!-- 3. Purchases -->
        <section id="kaeufe" class="mb-8">
          <div class="text-overline text--secondary">
            {{ $t('shop.userDetails.section.purchases') }}
          </div>
          <v-card outlined>
            <v-data-table
              :headers="purchaseHeaders"
              :items="purchaseRows"
              :items-per-page="-1"
              hide-default-footer
              disable-sort
              :no-data-text="$t('shop.userDetails.purchases.empty')"
            >
              <template v-slot:item.name="{ item }">
                <div>{{ purchaseTitles(item) }}</div>
                <v-chip
                  v-if="purchaseLabel(item)"
                  x-small
                  :color="purchaseColor(item)"
                  class="mt-1"
                >
                  {{ purchaseLabel(item) }}
                </v-chip>
              </template>
              <template v-slot:item.type="{ item }">
                <v-chip small outlined>{{ purchaseType(item) }}</v-chip>
              </template>
              <template v-slot:item.amount="{ item }">
                <div class="text-right">{{ formatAmount(item.amount) }}</div>
              </template>
              <template v-slot:item.date="{ item }">
                <div class="text-right">{{ formDate(item.created) }}</div>
              </template>
            </v-data-table>
          </v-card>
        </section>

        <!-- 4. Account and data -->
        <section id="konto" class="mb-8">
          <div class="text-overline text--secondary">
            {{ $t('shop.userDetails.section.account') }}
          </div>
          <v-card outlined class="py-3">
            <!-- An admin sends the request to support, a consumer deletes the account -->
            <v-list-item class="px-6 py-4">
              <div class="d-flex align-center justify-space-between flex-grow-1">
                <div>
                  <div>{{ $t('shop.userDetails.account.delete') }}</div>
                  <div class="text-caption text--secondary">
                    {{ isTenant
                      ? $t('shop.userDetails.account.deleteAdminHint')
                      : $t('shop.userDetails.account.deleteHint') }}
                  </div>
                </div>
                <v-btn
                  outlined
                  color="error darken-2"
                  @click="isTenant ? openWarning('feedbackForm') : openWarning('kontoLoeschen')"
                >
                  {{ isTenant
                    ? $t('shop.userDetails.account.deleteAdminAction')
                    : $t('modal.account_delete.submit') }}
                </v-btn>
              </div>
            </v-list-item>
          </v-card>
        </section>

        <!-- Cancel dialog -->
        <v-dialog v-model="cancelSubscriptionDialog" max-width="440">
          <v-card>
            <v-card-title>{{ $t('shop.userDetails.dialog.cancelSubscriptionTitle') }}</v-card-title>
            <v-card-text>
              {{ $t('shop.userDetails.dialog.cancelSubscriptionBody') }}
              {{ $t('shop.userDetails.dialog.cancelSubscriptionWarning') }}
            </v-card-text>
            <v-card-actions>
              <v-spacer />
              <v-btn text @click="cancelSubscriptionDialog = false">
                {{ $t('shop.userDetails.button.stay') }}
              </v-btn>
              <v-btn depressed color="error darken-2" @click="confirmCancel">
                {{ $t('shop.userDetails.button.confirmCancel') }}
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import userRole from "@/services/userRoleService";
import { mapGetters } from "vuex";
import APIService from "@/services/api.service";
import AppService from "@/services/app.service";
import moment from "moment";

export default {
  name: "UserProfile",

  data() {
    return {
      // Row in edit mode: name, email, pw, dob or null
      editRow: null,
      sections: [
        { id: "profil", label: "shop.userDetails.section.personalData" },
        { id: "abo", label: "shop.userDetails.section.subscription" },
        { id: "kaeufe", label: "shop.userDetails.section.purchases" },
        { id: "konto", label: "shop.userDetails.section.account" },
      ],
      activeSection: "profil",
      activeTab: 0,
      scrollObserver: null,

      // Date picker
      birthdateMenu: false,
      birthday: "",
      activePicker: null,

      // Form values
      password: "",
      profileImg: null,
      avatarKey: 0,
      showPwIcon: false,

      // Temporary values for the edit forms
      tempValues: {
        name: "",
        email: "",
      },

      // Address values of the webshop user, tenant only
      form: {
        address: "",
        town: "",
        zip: "",
        country: "",
      },

      // Validation states
      valid: {
        email: false,
        password: false,
        name: false,
        birthdate: false,
        address: false,
        town: false,
        zip: false,
        country: false,
      },

      addressRules: [
        (v) => !!v || this.$t("user.profile.validation.address_required"),
        (v) =>
          /^[a-zA-ZäöüÄÖÜß\s]+\s?\d+[a-zA-Z]?$/.test(v) ||
          this.$t("user.profile.validation.address_invalid"),
      ],

      zipRules: [
        (v) => !!v || this.$t("user.profile.validation.zip_required"),
        (v) => (!isNaN(parseFloat(v)) && !isNaN(v)) || this.$t("user.profile.validation.zip_numeric"),
        (v) => parseFloat(v) >= 0 || this.$t("user.profile.validation.zip_positive"),
      ],

      // Dialog states
      cancelSubscriptionDialog: false,
      pendingCancelId: null,

      // Validation rules
      eMailRules: [
        (v) => !!v || this.$t("shop.userDetails.validation.emailRequired"),
        (v) =>
          !v ||
          /^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,3})+$/.test(v) ||
          this.$t("shop.userDetails.validation.emailFormat"),
      ],

      passwordRules: [
        (v) => !!v || this.$t("shop.userDetails.validation.passwordRequired"),
        (v) => (v && v.length >= 6) || this.$t("shop.userDetails.validation.passwordMinLength"),
      ],

      userNameRules: [
        (v) => !!v || this.$t("shop.userDetails.validation.nameRequired"),
        (v) =>
          (v && v.length <= 30) ||
          this.$t("shop.userDetails.validation.nameMaxLength"),
        (v) =>
          (v && v.length >= 2) ||
          this.$t("shop.userDetails.validation.nameMinLength"),
      ],
    };
  },

  computed: {
    ...mapGetters(["get"]),

    oneUser() {
      return this.$store.state.main?.user?.result || {};
    },

    authUser() {
      return this.$store.state.main?.auth_user?.result || {};
    },

    subscriptionRows() {
      const rows = this.oneUser?.subscriptions || [];
      return [...rows].sort((a, b) => Number(b.id) - Number(a.id));
    },

    purchaseRows() {
      const rows = this.oneUser?.orders || [];
      return [...rows].sort((a, b) => String(b.created).localeCompare(String(a.created)));
    },

    purchaseHeaders() {
      return [
        { text: this.$t("shop.userDetails.label.item"), value: "name" },
        { text: this.$t("shop.userDetails.label.art"), value: "type" },
        { text: this.$t("shop.userDetails.label.amount"), value: "amount", align: "end" },
        { text: this.$t("shop.userDetails.label.date"), value: "date", align: "end" },
      ];
    },

    // A tenant is the admin of the instance
    isTenant() {
      return this.currentUser(["ROLE_TENANT"]);
    },

    // Address rows, tenant only
    addressFields() {
      return [
        { key: "address", label: "user.profile.address.label_static", title: "user.profile.address.change_title", input: "user.profile.address.label", rules: this.addressRules },
        { key: "town", label: "user.profile.town.label_static", title: "user.profile.town.change_title", input: "user.profile.town.label" },
        { key: "zip", label: "user.profile.zip.label_static", title: "user.profile.zip.change_title", input: "user.profile.zip.label", rules: this.zipRules },
        { key: "country", label: "user.profile.country.label_static", title: "user.profile.country.change_title", input: "user.profile.country.label" },
      ];
    },

    birthdayRules() {
      return [
        (v) => !!v || this.$t("shop.userDetails.validation.birthdayRequired"),
        (v) => {
          if (!v) return true;
          const birthDate = new Date(v);
          const today = new Date();
          let age = today.getFullYear() - birthDate.getFullYear();
          const monthDiff = today.getMonth() - birthDate.getMonth();
          if (
            monthDiff < 0 ||
            (monthDiff === 0 && today.getDate() < birthDate.getDate())
          ) {
            age--;
          }
          return age >= 18 || this.$t("shop.userDetails.validation.ageMinimum");
        },
      ];
    },
  },

  watch: {
    birthdateMenu(val) {
      val && setTimeout(() => (this.activePicker = "YEAR"));
    },
  },

  methods: {
    // Utility methods
    currentUser(roles) {
      return userRole.hasRole(roles);
    },

    formDate(value) {
      if (value) {
        return moment(String(value)).format("DD.MM.YYYY");
      }
      return "—";
    },

    // The date picker keeps the ISO value, the field shows the German format
    dateInput(value) {
      return value ? moment(String(value)).format("DD.MM.YYYY") : "";
    },

    // Betraege sind netto gespeichert. Die Anzeige rechnet 19 Prozent auf
    // (Regel von Kevin 2026-10-02).
    formatAmount(value) {
      const gross = Number(value) * 1.19;
      return new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR" })
        .format(isFinite(gross) ? gross : 0);
    },

    planAmount(sub) {
      const price = sub && sub.plan ? sub.plan.price : null;
      return price === null || price === undefined ? "—" : this.formatAmount(price);
    },

    paymentMethod(sub) {
      if (sub && sub.payment && sub.payment.card_type) {
        return `${sub.payment.card_type} **** ${sub.payment.last4}`;
      }
      return "—";
    },

    remainingDays(value) {
      if (!value) return null;
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const days = Math.ceil((new Date(value) - today) / 86400000);
      return days > 0 ? days : null;
    },

    isRunning(sub) {
      return !!sub && sub.status === "active" && !sub.cancelled_at;
    },

    canCancel(sub) {
      // A booked cancel needs the withdraw, not a second cancel.
      return this.isRunning(sub) && !sub.cancel_dispatch_at;
    },

    canReactivate(sub) {
      if (!sub) return false;
      const inactive = sub.status === "cancelled" || sub.status === "suspended";
      return inactive && !AppService.isSubscriptionUsable(sub);
    },

    askCancel(subId) {
      this.pendingCancelId = subId;
      this.cancelSubscriptionDialog = true;
    },

    confirmCancel() {
      this.cancelSubscriptionDialog = false;
      if (this.pendingCancelId) {
        this.cancelSubscription(this.pendingCancelId);
      }
    },

    // The cancel is a booking: the provider cancel runs in the cron, three days
    // before the billing date. This route clears the booking.
    withdrawCancel(subId) {
      this.$store
        .dispatch("patch", {
          endpoint: `/api/v1/subscriptions/${subId}/withdraw-cancel`,
          event: {},
          message: false,
        })
        .then(() => {
          APIService.setResponse({
            message: this.$t("shop.userDetails.message.cancelWithdrawn"),
            color: "green",
          });
          this.$store.dispatch("fetch", {
            endpoint: `/api/v1/users/${this.oneUser.id}`,
            state: "user",
          });
        })
        .catch((error) => {
          APIService.setResponse({
            message: `${this.$t("shop.userDetails.message.cancelError")}: ${error.message}`,
            color: "error",
          });
        });
    },

    // The base theme colours do not reach 4.5:1 on white, so the darken
    // variants carry the chip text.
    stateLabel(status) {
      const key = "shop.userDetails.state." + String(status || "").toLowerCase();
      const label = this.$t(key);
      return label === key ? String(status || "—") : label;
    },

    stateColor(status) {
      if (status === "active") return "success darken-3";
      if (status === "cancelled") return "deep-orange darken-4";
      if (status === "scheduled") return "primary darken-3";
      if (status === "pending" || status === "past_due") return "deep-orange darken-4";
      return "grey darken-2";
    },

    purchaseTitles(order) {
      const videos = (order.videos || []).map((video) => video.title);
      const images = (order.images || []).map((image) => image.title);
      const titles = videos.concat(images).filter((title) => !!title);
      return titles.length ? titles.join(", ") : "—";
    },

    purchaseType(order) {
      const videos = (order.videos || []).length;
      const images = (order.images || []).length;
      if (videos && images) return this.$t("shop.userDetails.type.mixed");
      if (videos) return this.$t("shop.userDetails.type.video");
      if (images) return this.$t("shop.userDetails.type.image");
      return "—";
    },

    // The payment state is shown only when it is not plain "paid".
    purchaseLabel(order) {
      if (order.failureReason) return this.$t("shop.userDetails.purchaseState.failed");
      if (order.order_paid === false) return this.$t("shop.userDetails.purchaseState.unpaid");
      return null;
    },

    purchaseColor(order) {
      if (order.failureReason) return "error darken-2";
      return "deep-orange darken-4";
    },

    // Navigation
    goTo(id) {
      const index = this.sections.findIndex((section) => section.id === id);
      if (index >= 0) this.activeTab = index;
      this.activeSection = id;
      this.$vuetify.goTo(`#${id}`, { duration: 300, offset: 88 });
    },

    openEdit(row) {
      this.editRow = row;
      if (row === "name") this.tempValues.name = this.oneUser.name;
      if (row === "email") this.tempValues.email = this.get("auth_user").email;
      if (row === "pw") this.password = "";
      if (row === "dob") {
        this.birthday = this.oneUser.birthday
          ? moment(this.oneUser.birthday).format("YYYY-MM-DD")
          : "";
      }
      if (row === "address" || row === "town" || row === "zip" || row === "country") {
        this.form[row] = this.get("webshop_user")[row];
      }
    },

    closeEdit() {
      this.editRow = null;
      this.password = "";
      this.showPwIcon = false;
    },

    openWarning(name) {
      this.$store.dispatch("modal/open", name);
    },

    // Update methods
    updateName() {
      if (!this.valid.name) return;

      this.editUserProfile({ name: this.tempValues.name });
      this.closeEdit();
    },

    updateEmail() {
      if (!this.valid.email) return;

      this.editUserMail(this.authUser.id, this.tempValues.email);
      this.closeEdit();
    },

    updatePassword() {
      if (!this.valid.password) return;

      this.editUserPwd();
      this.closeEdit();
    },

    submitBirthday() {
      if (!this.valid.birthdate) return;

      const today = new Date();
      const birthDate = new Date(this.birthday);
      let age = today.getFullYear() - birthDate.getFullYear();
      const monthDiff = today.getMonth() - birthDate.getMonth();

      if (
        monthDiff < 0 ||
        (monthDiff === 0 && today.getDate() < birthDate.getDate())
      ) {
        age--;
      }

      if (age >= 18) {
        this.editUserProfile({ birthday: this.birthday + " 00:00:00" });
        this.closeEdit();
      } else {
        APIService.setResponse({
          message: this.$t("shop.userDetails.message.mustBe18"),
          color: "red",
        });
      }
    },

    editUserProfile(object) {
      if (this.authUser.email.length > 0) {
        this.$store
          .dispatch("patch", {
            endpoint: `/api/v1/users/${this.oneUser.id}`,
            event: object,
            message: false,
          })
          .then(() => {
            APIService.setResponse({
              message: this.$t("shop.userDetails.message.changesSaved"),
              color: "green",
            });
            this.$store.dispatch("fetch", {
              endpoint: `/api/v1/users/${this.oneUser.id}`,
              state: "user",
            });
          });
      }
    },

    editUserMail(id, email) {
      if (email && email.length > 0) {
        this.$store
          .dispatch("patch", {
            endpoint: `/api/v1/users/${id}`,
            event: { email: email },
            host: "getAuthHost",
            message: false,
          })
          .then(() => {
            APIService.setResponse({
              message: this.$t("shop.userDetails.message.emailSaved"),
              color: "success",
            });
            // Update local state
            // Der Auth-Host kennt kein /users/me: der eigene Eintrag kommt
            // ueber die ID (gleiches Muster wie App.vue::initializeUserData).
            this.$store.dispatch("fetch", {
              endpoint: `/api/v1/users/${id}`,
              host: "getAuthHost",
              state: "auth_user",
            });
          });
      }
    },

    editUserPwd() {
      if (this.password && this.password.length > 0) {
        const payload = {
          password: this.password,
          email: this.authUser.email
        };

        this.$store
          .dispatch("patch", {
            endpoint: `/api/v1/users/${this.authUser.id}`,
            event: payload,
            host: "getAuthHost",
            message: false,
          })
          .then(() => {
            APIService.setResponse({
              message: this.$t("shop.userDetails.message.passwordChanged"),
              color: "success",
            });
          });
      }
    },

    saveWebshopField(field) {
      if (!this.valid[field]) return;
      const payload = {};
      payload[field] = this.form[field];
      this.editWebShopUser(payload);
      this.closeEdit();
    },

    // The webshop host holds address, town, zip and country
    editWebShopUser(object) {
      const webshopUser = this.get("webshop_user");
      if (!webshopUser || !webshopUser.id) return;
      this.$store
        .dispatch("patch", {
          endpoint: `/api/v1/users/${webshopUser.id}`,
          event: object,
          host: "getWebshopHost",
          message: false,
        })
        .then(() => {
          this.$store.dispatch("fetch", {
            endpoint: `/api/v1/users/me`,
            state: "webshop_user",
            host: "getWebshopHost",
          });
          APIService.setResponse({
            message: this.$t("shop.userDetails.message.changesSaved"),
            color: "green",
          });
        });
    },

    profilBildUpload() {
      if (!this.profileImg) return;

      this.$store
        .dispatch("uploadFiles", {
          endpoint: `/api/v1/users/upload`,
          files: { image: this.profileImg },
          metadata: {},
          message: false,
        })
        .then(() => {
          this.$store.dispatch("fetch", {
            endpoint: `/api/v1/users/${this.oneUser.id}`,
            state: "user",
          }).then(() => {
            this.avatarKey++;
          });
          APIService.setResponse({
            message: this.$t("shop.userDetails.message.profileImageUploaded"),
            color: "success",
          });
          this.profileImg = null;
        });
    },

    imageSource(user, width, height) {
      return AppService.imageSource(user, width, height);
    },

    cancelSubscription(subId) {
      this.$store
        .dispatch("post", {
          endpoint: `/api/v1/subscriptions/${subId}/cancel`,
          message: false,
        })
        .then(() => {
          APIService.setResponse({
            message: this.$t("shop.userDetails.message.subscriptionCancelled"),
            color: "info",
          });
          // The route writes the state of the subscription. Show the new
          // status at once, then sync with the provider. The view needs no
          // page load.
          this.setSubscriptionStatus("cancelled");
          this.loadSubscriptionData();
        })
        .catch((error) => {
          APIService.setResponse({
            message: `${this.$t("shop.userDetails.message.cancelError")}: ${error.message}`,
            color: "error",
          });
        });
    },

    setSubscriptionStatus(status) {
      const subscription = this.get("subscription");
      if (!subscription || !subscription.id) return;
      this.$store.dispatch("set", {
        state: "subscription",
        data: {
          ...this.$store.state.subscription,
          result: { ...subscription, status: status },
        },
      });
    },

    reactivateSubscription(subId) {
      this.$store
        .dispatch("patch", {
          endpoint: `/api/v1/subscriptions/${subId}/activate`,
          message: false,
        })
        .then(() => {
          APIService.setResponse({
            message: this.$t("shop.userDetails.message.subscriptionReactivated"),
            color: "info",
          });
          // The route writes the state and reads the provider. Show the new
          // status at once, then sync with the provider.
          this.setSubscriptionStatus("active");
          this.loadSubscriptionData();
        })
        .catch((error) => {
          APIService.setResponse({
            message: `${this.$t("shop.userDetails.message.reactivateError")}: ${error.message}`,
            color: "error",
          });
        });
    },

    loadSubscriptionData() {
      const subs = this.$store.state.main?.user?.result?.subscriptions;
      if (subs?.length > 0) {
        // Der laufende Zugang zaehlt: die nutzbare Zeile, sonst die juengste.
        // Die alte Regel nahm immer die erste Zeile. Nach einem Upgrade ist das
        // die gekuendigte, und das Profil zeigte sie mit "Reaktivieren".
        const usable = subs.filter((sub) => AppService.isSubscriptionUsable(sub));
        const pool = usable.length ? usable : subs;
        const current = pool.reduce((a, b) =>
          Number(a.id) >= Number(b.id) ? a : b
        );
        const userSubId = current.id;
        this.$store
          .dispatch("fetch", {
            endpoint: `/api/v1/subscriptions/${userSubId}/payment`,
            state: "subscription",
          })
          .then((response) => {
            this.$store.dispatch("setPayment", response.result.payment);
          })
          .catch((error) => {
            console.error("Error loading subscription:", error);
          });
      }
    },

    tryLoadSubscription() {
      if (this.$store.state.main?.user?.result) {
        this.loadSubscriptionData();
      } else {
        this.unwatchUser = this.$store.watch(
          (state) => state.main?.user?.result,
          (val) => {
            if (val) {
              this.loadSubscriptionData();
              this.unwatchUser && this.unwatchUser();
            }
          }
        );
      }
    },

    // Highlights the section that is in view
    observeSections() {
      if (typeof IntersectionObserver === "undefined") return;
      this.scrollObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            const index = this.sections.findIndex(
              (section) => section.id === entry.target.id
            );
            if (index < 0) return;
            this.activeSection = this.sections[index].id;
            this.activeTab = index;
          });
        },
        { rootMargin: "-20% 0px -70% 0px" }
      );
      this.sections.forEach((section) => {
        const el = document.getElementById(section.id);
        if (el) this.scrollObserver.observe(el);
      });
    },
  },

  mounted() {
    this.tryLoadSubscription();
    if (this.$store.state.main.user.result.subject) {
      this.$store.dispatch("fetch", {
        endpoint: `/api/v1/users/${this.$store.state.main.user.result.subject}`,
        state: "auth_user",
        host: "getAuthHost",
      });
    }

    // Initialize birthday if exists
    if (this.oneUser.birthday) {
      this.birthday = moment(this.oneUser.birthday).format("YYYY-MM-DD");
    }

    this.$nextTick(() => this.observeSections());
  },

  beforeDestroy() {
    this.unwatchUser && this.unwatchUser();
    this.scrollObserver && this.scrollObserver.disconnect();
  },
};
</script>

<style scoped>
.min-width-0 {
  min-width: 0;
}

.avatar-upload {
  cursor: pointer;
}

.avatar-upload__overlay {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.45);
  color: #fff;
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.2s;
}

.avatar-upload:hover .avatar-upload__overlay,
.avatar-upload:focus .avatar-upload__overlay {
  opacity: 1;
}
</style>
